import React, { useMemo } from 'react';
import { Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { HotelPhotoCarousel } from '@shared/components/HotelPhotoCarousel';
import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import { Skeleton } from '@shared/components/Skeleton';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { AddedTag } from './AddedTag';

type ListingPhotosProps = Readonly<{
  listing: AccommodationListingDTO;
  isMobileOrTablet?: boolean;
  /**
   * Round only the TOP corners at phone widths, for a photo flush against a card's top edge.
   *
   * Off by default: the guest/admin/ota mobile tiles are full-bleed with no card around them, so
   * their photo carries its own radius on all four corners. Inside the cash-fund card the bottom
   * two left white notches where the photo met the content.
   */
  topOnlyRadiusOnMobile?: boolean;
  showAddedTag: boolean;
  children?: React.ReactNode;
}>;

const ListingPhotos = ({ listing, isMobileOrTablet, showAddedTag, topOnlyRadiusOnMobile, children }: ListingPhotosProps) => {
  const images = useMemo(() => {
    if (!listing.mediaAssets?.photos.length) {
      return [{ src: DEFAULT_HOTEL_IMAGE, alt: listing.name }];
    }

    return (
      listing.mediaAssets?.photos.map(x => ({
        src: x.url,
        alt: listing.name,
      })) || []
    );
  }, [listing.mediaAssets?.photos, listing.name]);

  if (isMobileOrTablet) {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        aspectRatio="16/9"
        maxWidth="100%"
        minWidth="100%"
        position="relative"
      >
        {showAddedTag && <AddedTag listing={listing} />}
        <HotelPhotoCarousel
          hotel={listing}
          images={images}
          context="Accommodations.ListingPhotos"
          aspectRatio="16/9"
          imgProps={{
            borderRadius: topOnlyRadiusOnMobile ? `${pxToRem(10)} ${pxToRem(10)} 0 0` : pxToRem(10),
          }}
          navigation={{
            variant: 'minimal',
            visible: 'default',
          }}
        >
          {children}
        </HotelPhotoCarousel>
      </Flex>
    );
  }

  // Desktop: no aspectRatio — alignSelf="stretch" fills the parent row height.
  const leftOnlyRadius = `${pxToRem(10)} 0 0 ${pxToRem(10)}`;
  return (
    <HotelPhotoCarousel
      hotel={listing}
      images={images}
      context="Accommodations.ListingPhotos"
      width={pxToRem(278)}
      flexShrink={0}
      alignSelf="stretch"
      borderRadius={leftOnlyRadius}
      imgProps={{
        borderRadius: leftOnlyRadius,
      }}
      navigation={{
        variant: 'minimal',
        visible: 'default',
      }}
    >
      {showAddedTag && <AddedTag listing={listing} />}
      {children}
    </HotelPhotoCarousel>
  );
};

// `topOnlyRadiusOnMobile` mirrors the real photo's prop: a placeholder with four rounded corners
// under a card's top edge leaves the same white notches the photo did.
ListingPhotos.Skeleton = ({ isMobileOrTablet = true, topOnlyRadiusOnMobile }: { isMobileOrTablet?: boolean; topOnlyRadiusOnMobile?: boolean }) => {
  const leftOnlyRadius = `${pxToRem(10)} 0 0 ${pxToRem(10)}`;
  if (!isMobileOrTablet) {
    return (
      <Skeleton
        width={pxToRem(278)}
        flexShrink={0}
        alignSelf="stretch"
        borderRadius={leftOnlyRadius}
      />
    );
  }
  return (
    <Skeleton
      width="100%"
      aspectRatio="16/9"
      borderRadius={topOnlyRadiusOnMobile ? `${pxToRem(10)} ${pxToRem(10)} 0 0` : pxToRem(10)}
    />
  );
};

export { ListingPhotos };
